// @flow

import React from "react";
import { compose } from "redux";
import { connect } from "react-redux";
import { localize } from "redux-i18n";
import Ajv from "ajv";

import sharedActionCreators from "@modules/shared/actionCreator";
import * as sharedSelectors from "@modules/shared/selectors";
import type { AbsenceReason } from "@modules/shared/reducer/absence-reasons/types";
import type { HierarchyTree } from "@modules/shared/reducer/hierarchy/types";
import actionCreators from "@modules/service/actionCreator";
import * as serviceSelectors from "@modules/service/selectors";
import {
  Button,
  Modal,
  ModalBody,
  ModalHeader,
  ModalFooter,
  CollapsibleWizard,
  Step,
} from "@components";
import type { HierarchyAssignment } from "@modules/service/types";
import type { EmailNotification } from "@modules/service/reducer/email-notifications/types";

import Step1Content from "./step1-content";
import Step2Reasons from "./step2-reasons";
import Step3Rules from "./step3-rules";
import StepAssignments from "./step-assignments";

type OwnProps = $Exact<{
  defaultStep: 1 | 2 | 3 | 4,
  clientId: number,
}>;

type PropsFromState = $Exact<{
  schema: ?Object,
  reasons: Array<AbsenceReason>,
  current: ?EmailNotification,
  edited: ?EmailNotification,
  assignments: Array<HierarchyAssignment>,
  hierarchy: ?HierarchyTree,
  isAssignmentsValid: boolean,
}>;

type PropsFromDispatch = $Exact<{
  getReasons: typeof sharedActionCreators.GetAbsenceReasons,
  setEdited: typeof actionCreators.SetEditedEmailNotification,
  setAssignments: typeof actionCreators.SetEmailNotificationAssignments,
  update: typeof actionCreators.UpdateEmailNotification,
  setShowModal: typeof actionCreators.SetShowEditEmailNotificationModal,
}>;

type Props = $Exact<{
  ...OwnProps,
  ...PropsFromLocalize,
  ...PropsFromState,
  ...PropsFromDispatch,
}>;

export class EmailNotificationEditModal extends React.Component<Props> {
  validate: *;

  componentDidMount() {
    this.validate = new Ajv().compile(this.props.schema);
    this.props.getReasons({ clientId: this.props.clientId });
  }

  get isValid() {
    if (!this.validate) {
      return false;
    }

    const valid = this.validate(this.props.edited);
    if (!valid) {
      console.warn("isValid errors", ...this.validate.errors);
    }
    return valid;
  }

  get isDefault() {
    return (this.props.current || {}).isDefault;
  }

  handleCancel = () => {
    this.props.setShowModal({ show: false });
  };

  handleSubmit = () => {
    this.props.update({
      clientId: this.props.clientId,
      rule: this.props.edited,
      assignments: this.props.assignments,
      isAssigmentsRequired: true,
    });
  };

  handleChange = ([step1, step2, step3, step4]: Array<Object> = []) => {
    this.props.setEdited({
      ...this.props.edited,
      ...step1,
      ...step2,
      ...step3,
    });

    this.props.setAssignments([...step4.assignments]);
  };

  get stepData() {
    return [
      {
        data: {
          name: (this.props.current || {}).name,
          includeEmployeePhoneNumber: (this.props.current || {}).includeEmployeePhoneNumber,
          includeWorkingHours: (this.props.current || {}).includeWorkingHours,
          includeBradfordScore: (this.props.current || {}).includeBradfordScore,
          includeJobTitle: (this.props.current || {}).includeJobTitle,
          includeAbsenceId: (this.props.current || {}).includeAbsenceId,
          includeLastThreeAbsences: (this.props.current || {}).includeLastThreeAbsences,
          includeDepartmentParent: (this.props.current || {}).includeDepartmentParent,
          includeEmployeeRef: (this.props.current || {}).includeEmployeeRef,
          includePosition: (this.props.current || {}).includePosition,
          startInstruction: (this.props.current || {}).startInstruction || undefined,
          updateNotification: (this.props.current || {}).updateNotification || undefined,
          closedInstruction: (this.props.current || {}).closedInstruction || undefined,
          overdueInstruction:
            (this.props.current || {}).overdueInstruction ||
            this.props.t("OVERDUE_ABSENCE_DEFAULT_TEXT"),
        },
        valid: true,
      },
      {
        data: {
          reasonSpecificInstructions: (this.props.current || {}).reasonSpecificInstructions || [],
        },
        valid: true,
      },
      {
        data: {
          emailDistributionType: (this.props.current || {}).emailDistributionType,
          batchedEmailFrequency: (this.props.current || {}).batchedEmailFrequency,
          startNotifications: (this.props.current || {}).startNotifications || false,
          includeRtwDetailsInAbsenceNotifications: (this.props.current || {})
            .includeRtwDetailsInAbsenceNotifications,
          includeAlertsInAbsenceNotifications: (this.props.current || {})
            .includeAlertsInAbsenceNotifications,
        },
        valid: true,
      },
      {
        data: { assignments: this.props.assignments },
        valid: true,
      },
    ];
  }

  render() {
    return (
      <Modal className="modal-large">
        <ModalHeader className="d-flex justify-content-between" toggle={this.handleCancel}>
          {this.props.t("EMAIL_NOTIFICATION_EDIT_TITLE", { id: (this.props.current || {}).id })}
        </ModalHeader>
        <ModalBody className="d-flex flex-column justify-content-start align-content-center">
          {this.props.schema ? (
            <CollapsibleWizard
              defaultStep={this.props.defaultStep}
              data={this.stepData}
              onChange={this.handleChange}
            >
              <Step
                component={Step1Content}
                title={this.props.t("EMAIL_NOTIFICATION_STEP1")}
                schema={this.props.schema}
                edited={this.props.edited}
                editing={true}
              />
              <Step
                component={Step2Reasons}
                title={this.props.t("EMAIL_NOTIFICATION_STEP2")}
                schema={this.props.schema}
                edited={this.props.edited}
                reasons={this.props.reasons}
                editing={true}
              />
              <Step
                component={Step3Rules}
                title={this.props.t("EMAIL_NOTIFICATION_STEP3")}
                schema={this.props.schema}
                edited={this.props.edited}
                editing={true}
              />
              <Step
                component={StepAssignments}
                title={this.props.t("EMAIL_NOTIFICATION_STEP4")}
                hierarchy={this.props.hierarchy}
                assignments={this.props.assignments}
                hidden={this.isDefault}
                editing={true}
              />
            </CollapsibleWizard>
          ) : null}
        </ModalBody>
        <ModalFooter className="d-flex justify-content-end">
          <Button color="secondary" onClick={this.handleCancel}>
            {this.props.t("CANCEL")}
          </Button>
          <Button
            materialIcon="save"
            disabled={!(this.isValid && this.props.isAssignmentsValid)}
            onClick={this.handleSubmit}
          >
            {this.props.t("SAVE")}
          </Button>
        </ModalFooter>
      </Modal>
    );
  }
}

const mapStateToProps = (state, ownProps): PropsFromState => {
  return {
    schema: serviceSelectors.getEmailNotificationSchema(state),
    reasons: sharedSelectors.getAbsenceReasons(state),
    current: serviceSelectors.getCurrentEmailNotification(state),
    edited: serviceSelectors.getEditedEmailNotification(state),
    assignments: serviceSelectors.getEmailNotificationAssignments(state),
    hierarchy: sharedSelectors.getHierarchyTree(state),
    isAssignmentsValid: serviceSelectors.isEmailNotificationAssignmentsValid(state),
  };
};

const mapDispatchToProps: PropsFromDispatch = {
  getReasons: sharedActionCreators.GetAbsenceReasons,
  setEdited: actionCreators.SetEditedEmailNotification,
  setAssignments: actionCreators.SetEmailNotificationAssignments,
  update: actionCreators.UpdateEmailNotification,
  setShowModal: actionCreators.SetShowEditEmailNotificationModal,
};

export default compose(
  localize(),
  connect(mapStateToProps, mapDispatchToProps),
)(EmailNotificationEditModal);
